import React, { useRef, useState } from 'react';
import { CircleStencil, FixedCropper, ImageRestriction } from 'react-advanced-cropper';
import { getAbsoluteZoom, getZoomFactor } from 'advanced-cropper/extensions/absolute-zoom';
import { FormattedMessage } from '../../../util/reactIntl';
import { ModalPortal, Button } from '../..';
import { Slider } from '../Slider';
import css from './EditImageViaCropperModal.css';
import { useUploadImageWizardContext } from '../UploadImageWizard';
import { IconCheckCircleL, IconReplaceL, IconZoomInL, IconZoomOutL } from '../../../icons';

const isNumber = value => typeof value === 'number' && !Number.isNaN(parseInt(value));

export default ({ src, zoom, setZoom, onClose, onImageUpload, goToList, uploadInProgress }) => {
    const { setStrategy, setUploadState, imageName } = useUploadImageWizardContext();

    const cropperRef = useRef();
    const requestedRef = useRef(false);
    const [requested, setRequested] = useState(false);

    const resolveAbsoluteZoomFromRef = () =>
        cropperRef && cropperRef.current
            ? getAbsoluteZoom(cropperRef.current.getState(), cropperRef.current.getSettings())
            : undefined;

    const onUpdate = async () => {
        if (!cropperRef || !cropperRef.current) {
            return;
        }
        if (uploadInProgress || requestedRef.current) return;

        requestedRef.current = true;
        setRequested(true);

        try {
            const { current } = cropperRef;

            const canvas = current.getCanvas();
            const formData = new FormData();

            const blob = await new Promise(res => canvas.toBlob(res));

            formData.append('file', blob, `${imageName || 'unnamed'}.jpeg`);
            await onImageUpload(formData.get('file'), { setStrategy, setUploadState });
        } finally {
            requestedRef.current = false;
            setRequested(false);
        }
    };

    const onZoom = (value, transitions) => {
        if (cropperRef && cropperRef.current) {
            const { current } = cropperRef;

            current.zoomImage(getZoomFactor(current.getState(), current.getSettings(), value), {
                transitions,
            });

            setZoom(value);
        }
    };

    const onZoomIn = () => {
        const absoluteZoom = resolveAbsoluteZoomFromRef();

        if (onZoom && isNumber(absoluteZoom)) {
            onZoom(Math.min(1, absoluteZoom + 0.125), true);
        }
    };

    const onZoomOut = () => {
        const absoluteZoom = resolveAbsoluteZoomFromRef();

        if (onZoom && isNumber(absoluteZoom)) {
            onZoom(Math.max(0, absoluteZoom - 0.125), true);
        }
    };

    return (
        <ModalPortal
            id="EditUserAvatar"
            isOpen
            onClose={onClose}
            containerClassName={css.modalContainer}
            contentClassName={css.modalContent}
            containerClassNameJoined
        >
            <h3 className={css.editorHeading}>Positionieren</h3>
            <ul className={css.instructions}>
                <li>
                    <p>
                        <IconCheckCircleL /> Nutze keine Effekte
                    </p>
                </li>
                <li>
                    <p>
                        <IconCheckCircleL /> Zeige nur dein Gesicht
                    </p>
                </li>
                <li>
                    <p>
                        <IconCheckCircleL /> Schaue in die Kamera
                    </p>
                </li>
            </ul>
            <div className={css.editorHolder}>
                <code className={css.interruptEditing} onClick={goToList}>
                    <IconReplaceL />
                </code>
                <FixedCropper
                    ref={cropperRef}
                    src={src}
                    stencilComponent={CircleStencil}
                    imageRestriction={ImageRestriction.stencil}
                    stencilSize={{
                        width: 250,
                        height: 250,
                    }}
                    stencilProps={{
                        handlers: false,
                        lines: false,
                        movable: false,
                        resizable: false,
                    }}
                    onChange={({ getState, getSettings }) => {
                        const absoluteZoom = getAbsoluteZoom(getState(), getSettings());
                        setZoom(absoluteZoom);
                    }}
                />
            </div>
            <div className={css.sliderSection}>
                <Slider value={zoom} onChange={onZoom} />
            </div>
            <div className={css.zoomSection}>
                <div onClick={onZoomOut}>
                    <IconZoomOutL />
                </div>
                <div onClick={onZoomIn}>
                    <IconZoomInL />
                </div>
            </div>

            <Button
                onClick={() => onUpdate(cropperRef)}
                className={css.actionButton}
                inProgress={uploadInProgress || requested}
                disabled={uploadInProgress || requested}
            >
                <FormattedMessage id="ProfileSettingsForm.editUserInfoAction-avatar" />
            </Button>
        </ModalPortal>
    );
};
